/**
 * Informative, self-repairing `run_code` click failures (fast-drive).
 *
 * When a fast-drive `browser_run_code` snippet's click fails with a
 * strict-mode violation (locator matched >1) or an actionability timeout
 * (matched a hidden/0-size element, or one occluded/off-screen), the raw
 * Playwright error is a dead end. This turns it into a next move: parse the
 * anchor text out of the failing locator, run the `browser_scan` engine on it,
 * and — taking ONLY non-destructive actions (scroll into view + `click({ trial:
 * true })`, never the real click) — hand back the actionable control's durable
 * locator plus a "retry with this" hint.
 *
 * The model retries on an already-revealed, confirmed target, and reports the
 * CORRECT durable locator as `cacheCode` (the failing one is never cached). No
 * silent substitution: we diagnose and advise, we do not click for the agent.
 *
 * @module playwright-client/run-code-failure-repair
 */

import type { Page } from 'playwright';
import type { RaceAgainstSignal } from './fast-drive-signals.js';
import { runFastDriveScan } from './fast-drive-scan.js';

/** Marker prefix so the tool layer can flag `disambiguationTriggered` telemetry. */
export const SCAN_REPAIR_MARKER = '↳ browser_scan:';

const STRICT_MODE_RE = /strict mode violation/i;
const TIMEOUT_RE = /Timeout\s+\d+ms exceeded|exceeded\b/i;
const ACTIONABILITY_RE =
  /waiting for|element is not visible|not stable|intercepts pointer|outside of the viewport|element is not enabled|not clickable|scrollIntoView/i;
// Playwright prefixes every locator error with the failing method, e.g.
// `locator.click:` / `mouse.click:` — so the error itself says whether a CLICK
// failed. This is the load-bearing gate: without it, a non-click op that
// strict-mode-violates or times out on actionability (e.g. `getByText('Saved')
// .waitFor()`, `.fill()`, `.hover()`) would wrongly get a "retry your click" hint.
const CLICK_ERR_RE = /\.(?:click|dblclick|tap)\b\s*[:(]/i;
/** The snippet actually performs a click (belt-and-suspenders alongside CLICK_ERR_RE). */
const CLICK_CODE_RE = /\.(?:click|dblclick|tap)\s*\(/i;

/**
 * Is this run_code error a CLICK that failed on ambiguity or actionability (as
 * opposed to a syntax error, a thrown assertion, a navigation, or a non-click
 * locator op like waitFor/fill/hover)? Gated on the error naming a click method
 * and — when the snippet is available — on the snippet actually clicking.
 */
function isClickFailure(msg: string, code?: string): boolean {
  if (typeof code === 'string' && !CLICK_CODE_RE.test(code)) return false;
  if (!CLICK_ERR_RE.test(msg)) return false;
  if (STRICT_MODE_RE.test(msg)) return true;
  return TIMEOUT_RE.test(msg) && ACTIONABILITY_RE.test(msg);
}

/**
 * Best-effort extraction of the text anchor from a failing Playwright locator in
 * the error message: `getByRole('...', { name: 'X' })`, `getByText('X')`,
 * `getByLabel('X')`, `getByPlaceholder('X')`. Returns the first usable name.
 */
export function extractAnchorText(msg: string): string | null {
  const roleName = msg.match(/getByRole\(\s*['"][^'"]+['"]\s*,\s*\{[^}]*\bname:\s*['"]([^'"]+)['"]/);
  if (roleName?.[1]) return roleName[1];
  const byText = msg.match(/getBy(?:Text|Label|Placeholder|AltText|Title)\(\s*['"]([^'"]+)['"]/);
  if (byText?.[1]) return byText[1];
  // `text=Foo` engine syntax
  const textEngine = msg.match(/\btext=(["']?)([^"'\n]{2,60})\1/);
  if (textEngine?.[2]) return textEngine[2].trim();
  return null;
}

/**
 * Diagnose a failed run_code click and, using only non-destructive scan actions,
 * return an appended hint block (with {@link SCAN_REPAIR_MARKER}) — or `null`
 * when the error isn't a click failure or no useful target could be found.
 */
export async function diagnoseRunCodeClickFailure(args: {
  errorMessage: string;
  /** The run_code snippet that failed — used to confirm it actually performs a click. */
  code?: string;
  getPage: () => Promise<Page>;
  raceAgainstSignal: RaceAgainstSignal;
  signal?: AbortSignal;
  logger?: { debug?: (msg: string, meta?: Record<string, unknown>) => void };
}): Promise<string | null> {
  const { errorMessage, code, getPage, raceAgainstSignal, signal, logger } = args;
  if (!isClickFailure(errorMessage, code)) return null;
  const query = extractAnchorText(errorMessage);
  if (!query) return null;

  let result;
  try {
    result = await runFastDriveScan({
      getPage,
      raceAgainstSignal,
      signal,
      logger,
      // The repair path never stamps and is reveal-only (no speculative trial-
      // click actionability wait) — the agent's own retry confirms actionability.
      input: { query, relation: 'self', allowReveal: true, allowStamp: false, revealOnly: true },
      stampingEnabled: false,
    });
  } catch {
    return null;
  }

  if (result.status === 'ready' && result.target) {
    return (
      `${SCAN_REPAIR_MARKER} that locator matched a hidden/ambiguous element. The actionable control is ` +
      `\`${result.target.locator}\` — scrolled into view. ` +
      `Retry your click with that locator (and report IT as cacheCode).`
    );
  }
  if (result.status === 'ambiguous' && result.candidates.length > 0) {
    const lines = result.candidates
      .slice(0, 3)
      .map((c, i) => `  ${i + 1}. ${c.role} "${c.name}"${c.anchor ? ` in ${c.anchor}` : ''} → ${c.locator}`)
      .join('\n');
    return (
      `${SCAN_REPAIR_MARKER} "${query}" matches ${result.candidates.length} controls — pick one and retry:\n${lines}`
    );
  }
  return null;
}
